import React, { Component } from 'react';
import { Popover, PopoverHeader, PopoverBody } from 'reactstrap';
import PropTypes from 'prop-types';
import Avatar from '../avatar/avatar';
import EmailContacts from './recently_messaged_users/email_contacts';
import Search from './recently_messaged_users/search';
import vapi from '../../javascript/frontend/api/vapi';
import vahoy from '../../javascript/vahoy';
import vlog from '../../javascript/vlog';

class RecentlyMessagedUsers extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentUser: null,
      helpPopoverOpen: false,
      autoFollowUp: false,
      data: [],
      isUpdating: false,
      searchTerm: '',
    };
  }

  async componentDidMount() {
    const currentUser = window.CURRENT_USER_JSON || null;
    const { maxContentItems } = this.props;

    if (!currentUser) return;

    try {
      // Get V2 profile ID from V1 profile ID using helper function
      const v2ProfileId = await window.v2ProfileId();

      if (!v2ProfileId) {
        vlog.error('Error fetching V2 profile ID for user');
        this.setState({
          currentUser,
          data: [],
          autoFollowUp: this.normalizedAutoFollowUp(currentUser),
        });
        return;
      }

      // Now query conversations using the V2 profile_id
      // Fetch more than we need since we'll deduplicate by sender
      const { data: conversations, error: conversationsError } = await window.supabaseClient
        .from('conversations')
        .select(`
          migrate_id,
          subject,
          last_sent_at,
          conversation_senders
        `)
        .eq('receiver_profile_id', v2ProfileId)
        .order('last_sent_at', { ascending: false })
        .limit(maxContentItems * 3); // Fetch extra to ensure enough unique senders

      if (conversationsError) {
        vlog.error('Error fetching conversations:', conversationsError.message);
        this.setState({
          currentUser,
          data: [],
          autoFollowUp: this.normalizedAutoFollowUp(currentUser),
        });
        return;
      }

      if (!conversations || conversations.length === 0) {
        this.setState({
          currentUser,
          data: [],
          autoFollowUp: this.normalizedAutoFollowUp(currentUser),
        });
        return;
      }

      // Expand each conversation into one row per non-current-user sender,
      // deduplicated across conversations by sender id (most recent wins).
      const seenSenders = new Set();
      const formattedData = [];

      for (const conv of conversations) {
        const senders = (conv.conversation_senders || [])
          .filter((sender) => sender.id !== v2ProfileId && !seenSenders.has(sender.id));

        for (const sender of senders) {
          seenSenders.add(sender.id);
          formattedData.push({
            name: sender.name || 'Unknown User',
            profile_image_url: sender.profile_image_url || null,
            title_at_work_place: sender.title_at_workplace || '',
            username: sender.slug,
            last_conversation_with_user: `/users/${currentUser.attributes?.slug}/conversations/${conv.migrate_id}`,
            last_conversation_with_user_at: conv.last_sent_at,
          });

          if (formattedData.length >= maxContentItems) break;
        }

        if (formattedData.length >= maxContentItems) break;
      }

      this.setState({
        data: formattedData,
        currentUser,
        autoFollowUp: this.normalizedAutoFollowUp(currentUser),
      });
    } catch (err) {
      vlog.error('Unexpected error in componentDidMount:', err);
      this.setState({
        currentUser,
        data: [],
        autoFollowUp: this.normalizedAutoFollowUp(currentUser),
      });
    }
  }

  normalizedAutoFollowUp = (currentUser) => currentUser?.attributes?.pr_auto_follow_up === true
    || currentUser?.attributes?.pr_auto_follow_up === 'true';

  helpToggle = () => {
    this.setState((prevState) => ({
      helpPopoverOpen: !prevState.helpPopoverOpen,
    }));
  };

  handleAutoFollupChange = (e) => {
    const newValue = e.target.checked;

    if (this.state.currentUser !== null) {
      const user = {
        id: this.state.currentUser.id,
        attributes: {
          pr_auto_follow_up: newValue,
        },
      };

      // Do an optimistic update in state; we'll revert state later if we get an API failure
      // Disable the radio while the update is in flight
      this.setState(
        {
          isUpdating: true,
          autoFollowUp: newValue,
        },
        () => this.updateUser(user),
      );
    }
  };

  updateUser = (user) => {
    vapi.updateUser(user)
      .catch(() => this.setState({ autoFollowUp: !user.attributes.pr_auto_follow_up }))
      .finally(() => this.setState({ isUpdating: false }));

    vahoy.track('RecentlyMessagedUsers#updateUser');
  };

  trackClicks = () => {
    vahoy.track('RecentlyMessagedUsers#clickLastConversationLink');
  };

  filterConversations = (conversations, searchTerm) => {
    if (!searchTerm || !searchTerm.trim()) {
      return conversations;
    }

    const lowerTerm = searchTerm.toLowerCase();

    return conversations.filter((conversation) => {
      const nameMatch = conversation.name.toLowerCase().includes(lowerTerm);
      const employmentMatch = conversation.title_at_work_place
        && conversation.title_at_work_place.toLowerCase().includes(lowerTerm);
      return nameMatch || employmentMatch;
    });
  };

  handleSearchChange = (e) => {
    this.setState({ searchTerm: e.target.value });
  };

  buildUserInitials = (fullName) => fullName
      .split(' ')
      .filter((part) => part.trim())
      .map((part) => part[0])
      .join('')
      .toUpperCase();

  render() {
    const {
      currentUser, data, autoFollowUp, isUpdating, helpPopoverOpen, searchTerm,
    } = this.state;
    const sortedConversations = _.sortBy(data, (conversation) => -moment(conversation.last_conversation_with_user_at));
    const filteredConversations = this.filterConversations(sortedConversations, searchTerm);
    let autoFollowUpSwitch = null;

    if (currentUser !== null && currentUser.attributes.user_type_humanized === 'source') {
      autoFollowUpSwitch = (
        <div className="form-check form-switch test-auto-follow-up mt-3">
          <input
            checked={autoFollowUp}
            className="form-check-input"
            disabled={isUpdating}
            id="auto-follow-up"
            onChange={this.handleAutoFollupChange}
            type="checkbox"
          />
          <label
            className="form-check-label"
            htmlFor="auto-follow-up"
          >
            Auto follow-up
          </label>
          <span>
            <button id="help-popover" type="button" className="btn btn-link py-0 ps-1">
              <i className="fa-solid fa-circle-question" />
            </button>

            <Popover
              placement="bottom"
              isOpen={helpPopoverOpen}
              target="help-popover"
              trigger="legacy"
              toggle={this.helpToggle}
            >
              <PopoverHeader>Auto Follow-Up</PopoverHeader>
              <PopoverBody>
                <p>
                  Turn on this feature to have Qwoted automatically follow up with reporters if it&apos;s
                  been more than one month since you communicated.
                </p>
                <p>The messages will appear in your conversation stream.</p>
              </PopoverBody>
            </Popover>
          </span>
        </div>
      );
    }

    return ((currentUser !== null) && (sortedConversations.length > 0)) && (
      <div className="recently-messaged-users-card test-recently_messaged_users">
        <div className="recently-messaged-users-header d-flex justify-content-between align-items-center">
          <h4>My Contacts</h4>
          <a href="/my_contacts" className="view-all-link btn btn-link p-0">View All</a>
        </div>

        <EmailContacts
          button_text="Invite Contacts"
          button_class="invite-contacts-btn d-flex align-items-center justify-content-center"
        />

        <Search
          searchTerm={searchTerm}
          onSearchChange={this.handleSearchChange}
        />

        <div className="contacts-scroll-container">
          {
            filteredConversations.map((recentlyMessagedUser, index) => {
              const {
                last_conversation_with_user: lastConversationLink,
                profile_image_url: avatarUrl,
                name: userFullName,
                title_at_work_place: employmentsString,
                username,
              } = recentlyMessagedUser;
              const computedInitials = this.buildUserInitials(userFullName);
              return (
                <React.Fragment key={username}>
                  <a
                    href={lastConversationLink}
                    onClick={this.trackClicks}
                    className="my-messaging-contact"
                  >
                    <Avatar
                      avatarUrl={avatarUrl}
                      sizeClass="avatar"
                      avatarLetters={computedInitials}
                    />
                    <div className="my-messaging-contact-info">
                      <div className="my-messaging-contact-name">
                        {userFullName}
                      </div>
                      <div className="my-messaging-contact-employment">
                        {employmentsString}
                      </div>
                    </div>
                  </a>
                  {index < filteredConversations.length - 1 && (
                    <div className="contact-separator" />
                  )}
                </React.Fragment>
              );
            })
          }
        </div>

        {autoFollowUpSwitch}
      </div>
    );
  }
}

RecentlyMessagedUsers.propTypes = {
  maxContentItems: PropTypes.number,
};

RecentlyMessagedUsers.defaultProps = {
  maxContentItems: 8,
};

export default RecentlyMessagedUsers;
